import React, { useEffect } from 'react';
import {
  StyleSheet,
  Image,
  Document,
  Page,
  usePDF,
  View,
  Text,
} from '@react-pdf/renderer';
import { formatDate, formatPrice, downloadReport } from '../../utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { clearReports } from '../../pages/cash-action-details/CashActionDetails.state';
import { constants } from '@thecb/components';
import { IMAGE_ORIENTATIONS } from '../../utils/constants';
import fallbackLogo from '../../static/img/cityville-logo-blue.png';
import sharedReportStyles from './SharedReportStyles';

const styles = StyleSheet.create({
  ...sharedReportStyles,
  page: {
    ...sharedReportStyles.page,
    fontSize: 10,
  },
  title: {
    ...sharedReportStyles.title,
    fontSize: 18,
    marginBottom: 8,
  },
  lineItem: {
    marginBottom: 8,
  },
  table: {
    width: '100%',
  },
  trow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 8,
  },
  thead: {
    backgroundColor: '#E5E7EC',
    borderBottom: 0,
  },
  thAlignLeft: {
    width: '25%',
    textAlign: 'left',
  },
  thAlignRight: {
    width: '25%',
    textAlign: 'right',
  },
  tdAlignLeft: {
    width: '25%',
    textAlign: 'left',
  },
  tdAlignRight: {
    width: '25%',
    textAlign: 'right',
  },
  cbBrand: {
    position: 'absolute',
    bottom: 30,
    left: 250,
    right: 0,
    width: 100,
    height: 12,
  },
  flexContainerRow: {
    ...sharedReportStyles.flexContainerRow,
    padding: '4px 8px',
    backgroundColor: constants.colors.ATHENS_GREY,
  },
  smallDividerGrey: {
    ...sharedReportStyles.smallDividerGrey,
    margin: 0,
  },
  greyContainer: {
    ...sharedReportStyles.greyContainer,
    padding: 0,
  },
});

const CloseoutReportHeading = ({
  title,
  closed_at,
  created_at,
  location,
  cashier,
  batch,
  closeout,
  headerImg,
  portraitOrLandscapeLogo,
}) => (
  <View>
    {/*
    Logo Image src will fallback to Cityville for local development purposes so we don't need to run frontend-configuration-service (FCS) 
    for this PDF to generate. If you do run FCS locally, this will get an image from S3 based on the client_slug in the token.
    This client_slug maps to that client's FCS JSON file path and grabs the headerLogoLink value. Example: metadata-src/denver-co/pos/meta.jsonnet -> headerLogoLink
    */}
    <Image
      style={
        portraitOrLandscapeLogo === IMAGE_ORIENTATIONS.PORTRAIT
          ? styles.clientBrandPortrait
          : styles.clientBrandLandscape
      }
      src={headerImg ?? fallbackLogo}
    />
    <Text style={styles.title}>{title}</Text>
    <Text style={styles.lineItem}>Date Opened: {formatDate(created_at)}</Text>
    <Text style={styles.lineItem}>Date Closed: {formatDate(closed_at)}</Text>
    <Text style={styles.lineItem}>Location: {location}</Text>
    <Text style={styles.lineItem}>User: {cashier}</Text>
    <Text style={styles.lineItem}>Batch: {batch}</Text>
    <Text style={styles.lineItem}>Drawer History ID: {closeout}</Text>
  </View>
);

const CloseoutReportDocument = ({
  agency = {},
  report_heading = {},
  tender_type = {},
  checks_accepted = [],
  headerImg,
  portraitOrLandscapeLogo,
}) => (
  <Document>
    <Page style={styles.page}>
      <CloseoutReportHeading
        title="Tender Type Summary"
        {...report_heading}
        headerImg={headerImg}
        portraitOrLandscapeLogo={portraitOrLandscapeLogo}
      />

      <View style={styles.largeDividerBlack} />
      <View style={styles.table}>
        {/* table head */}
        <View style={[styles.trow, styles.thead]}>
          <Text>Tender Type</Text>
          <Text>Amount</Text>
        </View>

        {/* table body */}
        <View style={styles.trow}>
          <Text>Cash Deposit</Text>
          <Text>{formatPrice(tender_type.cash_deposit_total || 0)}</Text>
        </View>
        <View style={styles.greyContainer}>
          <View style={styles.flexContainerRow}>
            <Text>Opening Amount</Text>
            <Text>{formatPrice(tender_type.opening_amount || 0)}</Text>
          </View>
          <View
            style={{
              ...styles.flexContainerRow,
              ...styles.accentBackgroundColor,
            }}
          >
            <Text>Cash Drop</Text>
            <Text>{formatPrice(tender_type.drops || 0)}</Text>
          </View>
          <View style={styles.flexContainerRow}>
            <Text>Cash Entered</Text>
            <Text>{formatPrice(tender_type.cash || 0)}</Text>
          </View>
          <View
            style={{
              ...styles.flexContainerRow,
              ...styles.accentBackgroundColor,
            }}
          >
            <Text>Cash Expected</Text>
            <Text>{formatPrice(tender_type.expected_cash || 0)}</Text>
          </View>
          <View style={styles.flexContainerRow}>
            <Text>Over/Under</Text>
            <Text>{formatPrice(tender_type.over_under || 0)}</Text>
          </View>
          <View
            style={{
              ...styles.flexContainerRow,
              ...styles.accentBackgroundColor,
            }}
          >
            <Text>Float</Text>
            <Text>{formatPrice(tender_type.closing_float || 0)}</Text>
          </View>
        </View>
        <View style={styles.trow}>
          <Text>Check</Text>
          <Text>{formatPrice(tender_type.check || 0)}</Text>
        </View>
        <View style={styles.greyContainer}>
          <View style={styles.flexContainerRow}>
            <Text>Physical Checks</Text>
            <Text>
              {formatPrice(tender_type.processed_externally_check || 0)}
            </Text>
          </View>
          <View
            style={{
              ...styles.flexContainerRow,
              ...styles.accentBackgroundColor,
            }}
          >
            <Text>Check / ICL Checks</Text>
            <Text>
              {formatPrice(tender_type.processed_internally_check || 0)}
            </Text>
          </View>
        </View>
        <View style={styles.smallDividerGrey} />
        <View style={styles.trow}>
          <Text>Credit</Text>
          <Text>{formatPrice(tender_type.credit || 0)}</Text>
        </View>
        <View style={styles.smallDividerGrey} />
        <View style={styles.trow}>
          <Text>Wire Transfer</Text>
          <Text>{formatPrice(tender_type.wire_payment || 0)}</Text>
        </View>
        <View style={styles.smallDividerGrey} />
        <View style={styles.trow}>
          <Text>Total</Text>
          <Text>{formatPrice(tender_type.total || 0)}</Text>
        </View>
      </View>

      <Image style={styles.cbBrand} src="/img/citybase-product.png" />
    </Page>

    <Page style={styles.page}>
      <CloseoutReportHeading
        title="Agency Sales"
        {...report_heading}
        headerImg={headerImg}
        portraitOrLandscapeLogo={portraitOrLandscapeLogo}
      />

      <View style={styles.largeDividerBlack} />
      <View style={styles.table}>
        {/* table head */}
        <View style={[styles.trow, styles.thead]}>
          <Text>Agency</Text>
          <Text>Amount</Text>
        </View>

        {/* table body */}
        {Object.entries(agency).map(([agency, amount], index) => (
          <View style={styles.trow} key={index}>
            <Text>{agency}</Text>
            <Text>{formatPrice(amount)}</Text>
          </View>
        ))}
      </View>

      <Image style={styles.cbBrand} src="/img/citybase-product.png" />
    </Page>

    <Page style={styles.page}>
      <CloseoutReportHeading
        title="Check Sales"
        {...report_heading}
        headerImg={headerImg}
        portraitOrLandscapeLogo={portraitOrLandscapeLogo}
      />

      <View style={styles.largeDividerBlack} />
      <View style={styles.table}>
        {/* Table Head  */}
        <View style={[styles.trow, styles.thead]}>
          <Text style={styles.thAlignLeft}>Check Number</Text>
          <Text style={styles.thAlignRight}>Order ID</Text>
          <Text style={styles.thAlignRight}>Check Processing</Text>
          <Text style={styles.thAlignRight}>Amount</Text>
        </View>

        {/* Rows generated from check data */}
        {!checks_accepted.length && <Text>No Check Payments</Text>}
        {checks_accepted.length ? (
          checks_accepted.map((check, index) => (
            <View style={styles.trow} key={index}>
              <Text style={styles.tdAlignLeft}>{check.check_number}</Text>
              <Text style={styles.tdAlignRight}>{check.order_id}</Text>
              <Text style={styles.tdAlignRight}>
                {check.processed_externally ? 'Physical' : 'Check / ICL'}
              </Text>
              <Text style={styles.tdAlignRight}>
                {formatPrice(check.amount)}
              </Text>
            </View>
          ))
        ) : (
          <Text></Text>
        )}
        {checks_accepted.length ? (
          [
            { label: 'Total', checks: checks_accepted },
            {
              label: 'Physical Total',
              checks: checks_accepted.filter(
                (check) => check.processed_externally,
              ),
            },
            {
              label: 'Check / ICL Total',
              checks: checks_accepted.filter(
                (check) => !check.processed_externally,
              ),
            },
          ].map(({ label, checks }, index) => (
            <View key={index} style={styles.trow}>
              <Text style={styles.tdAlignLeft}>{label}</Text>
              <Text style={styles.tdAlignRight}></Text>
              <Text style={styles.tdAlignRight}></Text>
              <Text style={styles.tdAlignRight}></Text>
              <Text style={styles.tdAlignRight}>{checks.length}</Text>
              <Text style={styles.tdAlignRight}>
                {formatPrice(
                  checks.reduce((acc, val) => acc + parseFloat(val.amount), 0),
                )}
              </Text>
            </View>
          ))
        ) : (
          <Text></Text>
        )}
      </View>
      <Image style={styles.cbBrand} src="/img/citybase-product.png" />
    </Page>
  </Document>
);

const useCloseoutReportDownloader = (data, logoutAction) => {
  const dispatch = useDispatch();
  const {
    clientMetadata: { data: metadata },
  } = useSelector((state) => state.global);
  const [instance, update] = usePDF({
    document: (
      <CloseoutReportDocument {...data} headerImg={metadata?.headerLogoLink} />
    ),
  });
  if (Object.entries(data).length !== 0) {
    update();
  }

  useEffect(() => {
    if (instance.loading) return;
    if (instance.error) return;
    if (instance.url && Object.entries(data).length !== 0) {
      const report_heading = data.report_heading;
      const terminal = report_heading.location.split(': ')[1];
      downloadReport(
        'Closeout',
        terminal,
        report_heading.closed_at,
        instance.url,
      );

      if (logoutAction) logoutAction();
    }
  }, [instance, data]);
  useEffect(() => {
    return () => dispatch(clearReports());
  }, []);
};

export { CloseoutReportDocument, useCloseoutReportDownloader };
